import { Building2, CircleCheck, Hourglass, Inbox, LifeBuoy, ShieldAlert, Layers } from 'lucide-react';

import { QueryError } from '@/components/ui/QueryError';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { institutesApi } from '../institutes/api/institutes.api';
import { LIVE_TICKETS, platformSupportApi } from '../support/api/support.api';
import { AttentionList } from '../components/AttentionList';
import { DeckHero } from '../components/DeckHero';
import { DeckStatTile } from '../components/DeckStatTile';
import { FleetComposition } from '../components/FleetComposition';
import { ReachPanel } from '../components/ReachPanel';
import { SubscriptionIncomeStrip } from '../components/SubscriptionIncomeStrip';
import { SubscriptionPanel } from '../components/SubscriptionPanel';
import { SubscriptionRevenuePanel } from '../components/SubscriptionRevenuePanel';
import { ActivityTrendPanel } from '../components/ActivityTrendPanel';
import { AdoptionStrip } from '../components/AdoptionStrip';
import { LoginsPanel } from '../components/LoginsPanel';
import { TopFeaturesPanel } from '../components/TopFeaturesPanel';
import { UsagePanel } from '../components/UsagePanel';

/**
 * These counts take in schools and colleges together, while the institutes
 * list opens on one kind at a time — so the tile says what its number covers
 * rather than leave the list looking short.
 */
const INSTITUTE_TILE_HINT = 'Schools and colleges';

export function PlatformDashboardPage() {
  const user = useAuthStore((state) => state.user);
  const { data, isLoading, error, refetch } = useApiQuery(() => institutesApi.stats(), []);
  // One request for the money row and the revenue panel under it.
  const income = useApiQuery(() => institutesApi.subscriptionIncome(), []);
  // The support desk's own counts, unfiltered, so these tiles always agree with
  // the queue's. One row is the least the list endpoint will return.
  const tickets = useApiQuery(() => platformSupportApi.list({ page: 1, limit: 1 }), []);
  const ticketCounts = tickets.data?.counts;

  const stats = data ?? null;
  const total = stats?.totalInstitutes ?? 0;
  const shareOf = (count: number) => (total > 0 ? count / total : undefined);

  return (
    <div className="flex flex-col gap-6 pb-12 w-full max-w-[1360px] mx-auto">
      {/* 1. Hero banner (compact height) */}
      <DeckHero
        operatorName={user?.fullName ?? ''}
        operatorEmail={user?.email ?? ''}
        stats={stats}
        isLoading={isLoading}
      />

      {error && (
        <QueryError title="Could not load platform figures" error={error} onRetry={refetch} />
      )}
      {tickets.error && (
        <QueryError title="Could not load ticket counts" error={tickets.error} onRetry={tickets.refetch} />
      )}

      {/* 2. Platform Overview */}
      <section className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#5b52f9]" />
          <h2 className="text-[14.5px] font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            Platform overview
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
          <DeckStatTile
            label="Total institutes"
            hint={INSTITUTE_TILE_HINT}
            value={stats?.totalInstitutes ?? 0}
            icon={<Building2 className="h-5 w-5" />}
            isLoading={isLoading}
            to="/platform/institutes"
            linkLabel="See every institute"
          />
          <DeckStatTile
            label="Active"
            hint={INSTITUTE_TILE_HINT}
            value={stats?.activeInstitutes ?? 0}
            tone="success"
            icon={<CircleCheck className="h-5 w-5" />}
            share={shareOf(stats?.activeInstitutes ?? 0)}
            shareLabel="Share of every institute that is active"
            isLoading={isLoading}
            to="/platform/institutes?status=ACTIVE"
            linkLabel="See active institutes"
          />
          <DeckStatTile
            label="Pending setup"
            hint={INSTITUTE_TILE_HINT}
            value={stats?.pendingSetup ?? 0}
            tone="warning"
            icon={<Hourglass className="h-5 w-5" />}
            share={shareOf(stats?.pendingSetup ?? 0)}
            shareLabel="Share of every institute still being set up"
            isLoading={isLoading}
            to="/platform/institutes?status=PENDING_SETUP"
            linkLabel="See institutes still being set up"
          />
          <DeckStatTile
            label="Suspended"
            hint={INSTITUTE_TILE_HINT}
            value={stats?.suspendedInstitutes ?? 0}
            tone="critical"
            icon={<ShieldAlert className="h-5 w-5" />}
            share={shareOf(stats?.suspendedInstitutes ?? 0)}
            shareLabel="Share of every institute that is suspended"
            isLoading={isLoading}
            to="/platform/institutes?status=SUSPENDED"
            linkLabel="See suspended institutes"
          />
          <DeckStatTile
            label="Total tickets"
            value={ticketCounts?.raised ?? 0}
            icon={<LifeBuoy className="h-5 w-5" />}
            isLoading={tickets.isLoading}
            to="/platform/support"
            linkLabel="Open the support desk"
          />
          <DeckStatTile
            label="Open tickets"
            value={ticketCounts?.open ?? 0}
            tone={ticketCounts?.open ? 'warning' : 'success'}
            icon={<Inbox className="h-5 w-5" />}
            share={ticketCounts?.raised ? ticketCounts.open / ticketCounts.raised : undefined}
            shareLabel="Share of every ticket still open, in progress or waiting on the school"
            isLoading={tickets.isLoading}
            to={`/platform/support?status=${LIVE_TICKETS}`}
            linkLabel="See open tickets"
          />
        </div>
      </section>

      {/* 3. Subscriptions (income strip + yearly licence fees card) */}
      <section className="flex flex-col gap-3.5">
        <h2 className="text-[14.5px] font-bold text-slate-800 dark:text-slate-100 tracking-tight">
          Subscriptions
        </h2>
        <SubscriptionIncomeStrip stats={stats} isLoading={isLoading} income={income} />
        <SubscriptionRevenuePanel income={income} />
        <SubscriptionPanel stats={stats} isLoading={isLoading} />
      </section>

      {/* 4. Usage Strip */}
      <section className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#5b52f9]" />
          <h2 className="text-[14.5px] font-bold text-slate-800 dark:text-slate-100 tracking-tight">
            Usage
          </h2>
        </div>
        <AdoptionStrip />
      </section>

      {/* 5. Daily activity */}
      <ActivityTrendPanel />

      {/* 6. Side-by-side: Most used features & Login breakdown */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 items-stretch">
        <TopFeaturesPanel />
        <LoginsPanel />
      </div>

      {/* 7. Time in the app */}
      <UsagePanel />

      {/* 8. Bottom Overview Columns (stacked cleanly) */}
      <section className="grid grid-cols-1 gap-5 lg:grid-cols-3 items-start">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <FleetComposition stats={stats} isLoading={isLoading} />
          <AttentionList
            stats={stats}
            isLoading={isLoading || income.isLoading || tickets.isLoading}
            income={income.data ?? null}
            ticketCounts={ticketCounts ?? null}
          />
        </div>

        <div className="flex flex-col gap-4">
          <ReachPanel stats={stats} isLoading={isLoading} />
        </div>
      </section>
    </div>
  );
}
